import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { RegistrationUserDataPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/internationalization/client/x-tel-input.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { isSDInternMail } from '../payment/sd-email.js';
import { SDInstitutionEnum, type SDStammdaten } from '../stammdaten/sd-stammdaten-plugin.js';
import { SDInvoiceReceiverEnum } from '../payment/invoice-receiver.js';
import { InternalInfoBanner } from './helpers.js';

type stammdatenKeys = keyof EntityData<SDStammdaten>;

export const sdRegistrationUserDataPlugin: RegistrationUserDataPlugin<SDStammdaten> = {
  id: 'registration::user-data::sd',
  type: 'registration::user-data',
  render: (
    stammdatenController: GenericFieldFormController<SDStammdaten>,
    translationController: TranslationController<BHTranslationDictionary>,
    user: User,
  ) => {
    const genericFieldOption = (fieldName: string) =>
      ({
        placeholder: translationController.translate('sd', 'field', fieldName as any) as string,
        placeholderMode: 'static-floating',
      }) as const;

    return html`
      <x-flex space="lg">
        <x-flex space="sm">
          <x-tel-input
            ${stammdatenController.field('phoneNumber')}
            .placeholder=${translationController.translate('field', 'phoneNumber')}
          ></x-tel-input>

          ${stammdatenController.genericField(['matrikelnummer'], genericFieldOption('matrikelnummer'))}
          ${(['salutation', 'title', 'occupation'] as stammdatenKeys[]).map(fieldName =>
            stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
          )}
          ${stammdatenController.genericField(['department'], genericFieldOption('department'))}
          ${
            stammdatenController.document?.department === SDInstitutionEnum.SDEXTERN
              ? ['externalDepartmentUniversity', 'externalDepartmentDepartment'].map(fieldName =>
                  stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                )
              : nothing
          }
          ${
            stammdatenController.document?.department === SDInstitutionEnum.SDOTHERS
              ? ['otherDepartmentFurtherInformation'].map(fieldName =>
                  stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                )
              : nothing
          }
        </x-flex>
        <x-flex space="md">
          <tr-h4> Rechnungsadresse </tr-h4>
          <x-flex space="sm">
            ${
              stammdatenController.document?.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL
                ? InternalInfoBanner()
                : nothing
            }
            <x-dropdown-selection
              .selectables=${[
                ...(stammdatenController.document?.department === SDInstitutionEnum.SDMEDIZIN ||
                stammdatenController.document?.department === SDInstitutionEnum.SDUNIKLINIKUM ||
                !isSDInternMail(user.email)
                  ? []
                  : [SDInvoiceReceiverEnum.SDINTERNAL]),
                ...(stammdatenController.document?.department &&
                ![SDInstitutionEnum.SDMEDIZIN, SDInstitutionEnum.SDUNIKLINIKUM].includes(
                  stammdatenController.document?.department,
                ) &&
                isSDInternMail(user.email)
                  ? []
                  : [SDInvoiceReceiverEnum.SDEXTERNAL]),
                SDInvoiceReceiverEnum.SDPRIVATE,
              ]}
              placeholder="${translationController.translate('sd', 'field', 'invoiceReceiver')}*"
              ${stammdatenController.field('invoiceReceiver')}
              .renderString=${(invoiceReceiver?: string) =>
                invoiceReceiver ? translationController.translate('enums', invoiceReceiver) : ''}
            ></x-dropdown-selection>
            ${
              stammdatenController.document?.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL ||
              stammdatenController.document?.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL
                ? ['institution', 'division', 'postbox', 'attention'].map(fieldName =>
                    stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                  )
                : nothing
            }
            ${stammdatenController.genericField(
              ['address', 'streetAndHousenumber'],
              genericFieldOption('streetAndHousenumber'),
            )}
            <x-flex horizontal space="sm">
              ${stammdatenController.genericField(['address', 'zipCity', 'zip'], genericFieldOption('zip'))}
              <div style="flex: 1">
                ${stammdatenController.genericField(['address', 'zipCity', 'city'], genericFieldOption('city'))}
              </div>
            </x-flex>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  },
};
